import { CSSProperties, FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { FormIcon } from "@/components/forms-design-system/FormIcon";
import Image from "@/components/Image/Image";
import { AmplitudeEvents } from "@/constants/amplitude";
import { useDynamicThemeAwareVariables } from "@/features/FormV2/hooks/useDynamicThemeAwareVariables";
import { STEP_TO_ELEMENT_ID } from "@/features/LiveFormEditor/AnimatePreview/Arrow/ArrowContext";
import { FormBlock, FormBlockProps } from "@/forms-design-system/FormBlock/FormBlock";
import { FormTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { mergeSx } from "@/helpers/mergeSx";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Verified } from "@/icons/filled";

type ThemedOrganizationLogoAndNameBlockProps = Omit<FormBlockProps, "ref" | "variant" | "data-test"> & {
  name: string;
  logoSrc?: string;
  withFallbackImage?: boolean;
  withVerifiedByZeffyBadge?: boolean;
};

export const ThemedOrganizationLogoAndNameBlock: FC<ThemedOrganizationLogoAndNameBlockProps> = ({
  name,
  logoSrc,
  sx,
  withFallbackImage = false,
  withVerifiedByZeffyBadge = false,
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslation();
  const { logAmplitudeEvent } = useAmplitude();

  const {
    fallbackImageBackgroundColor,
    verifiedByZeffyBadgeBackgroundColor,
    verifiedByZeffyBadgeTextColor,
    verifiedByZeffyBadgeIconPrimaryColor,
    verifiedByZeffyBadgeIconSecondaryColor,
  } = useDynamicThemeAwareVariables(({ mode }) => ({
    fallbackImageBackgroundColor:
      mode === "light" ? theme.palette.forms.surface.quiet : theme.palette.forms.surface.supershy,
    verifiedByZeffyBadgeBackgroundColor:
      mode === "light" ? theme.palette.forms.surface.quiet : theme.palette.forms.surface.supershy,
    verifiedByZeffyBadgeTextColor:
      mode === "light" ? theme.palette.forms.text.brand : theme.palette.forms.text.moderate,
    verifiedByZeffyBadgeIconPrimaryColor: theme.palette.forms.text.brand,
    verifiedByZeffyBadgeIconSecondaryColor:
      mode === "light" ? theme.palette.forms.surface.reverse : theme.palette.forms.surface.supershy,
  }));

  const logoSize = isSmallScreen ? 28 : 40;

  const styles = mergeSx(
    {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      gap: { xs: 1, md: 1.5 },
      padding: { xs: theme.spacing(0.5, 1), md: theme.spacing(1, 2) },
      ...(withVerifiedByZeffyBadge && {
        borderTopLeftRadius: 0,
        ...((!logoSrc
          ? ((isSmallScreen && name.length <= 16) || name.length <= 11) && {
              borderTopRightRadius: 0,
            }
          : (isSmallScreen && name.length <= 11) || name.length < 7) && {
          borderTopRightRadius: 0,
        }),
      }),
    },
    sx
  );

  const logoStyles = {
    borderRadius: theme.radius(0.5),
  };

  return (
    <Stack>
      {withVerifiedByZeffyBadge && (
        <Box
          id="verified-by-zeffy-badge"
          sx={(theme) => ({
            backgroundColor: verifiedByZeffyBadgeBackgroundColor,
            color: verifiedByZeffyBadgeTextColor,
            padding: theme.spacing(0.2, 2),
            borderRadius: "8px 8px 0 0",
            width: "fit-content",
            alignSelf: "flex-start",
          })}
        >
          <FormTooltip
            title={t("VerifiedByZeffyTooltip")}
            placement={isSmallScreen ? "bottom" : "right"}
            onOpen={() => logAmplitudeEvent(AmplitudeEvents.ZeffyBadgeTooltipViewed)}
          >
            <Stack direction="row" alignItems="center" gap={1} margin={theme.spacing(0.5, 0)}>
              <FormIcon size="small">
                <Verified
                  style={
                    {
                      "--icon-color": verifiedByZeffyBadgeIconPrimaryColor,
                      "--secondary-color": verifiedByZeffyBadgeIconSecondaryColor,
                    } as CSSProperties
                  }
                />
              </FormIcon>
              <FormTypography variant="body2" sx={{ fontSize: isSmallScreen ? "12px" : "14px" }}>
                {t("VerifiedByZeffy")}
              </FormTypography>
            </Stack>
          </FormTooltip>
        </Box>
      )}

      <FormBlock id={STEP_TO_ELEMENT_ID["logo"]} variant="card" data-test="form-v2-logo-chip" {...rest} sx={styles}>
        {logoSrc ? (
          <Image src={logoSrc} alt={name} width={logoSize} height={logoSize} sx={logoStyles} />
        ) : withFallbackImage ? (
          <Box
            width={logoSize}
            height={logoSize}
            sx={mergeSx(logoStyles, { backgroundColor: fallbackImageBackgroundColor })}
          />
        ) : null}
        <FormTypography
          variant={isSmallScreen ? "subtitle2" : "h6"}
          vibe="intense"
          sx={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {name}
        </FormTypography>
      </FormBlock>
    </Stack>
  );
};
